/**
 * src/components/shared/breadcrumb/BreadcrumbActions.jsx
 * AC Nautitech — Voice Breadcrumbs
 *
 * Renders the actions list: undo bars, accepted actions, pending actions
 * (with inline accept forms), and dismissed actions (collapsible).
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Check, X, ChevronDown, ChevronUp, Loader2, Users, Clock,
  CheckCircle2, XCircle, ArrowRight, RotateCcw, Package, Sparkles,
} from 'lucide-react';
import { addProductCategory } from '../../../lib/api/categories';
import { useT } from '../../../lib/i18n/I18nContext';
import { TYPOGRAPHY, BREADCRUMB } from '../../../design-tokens';
import { suggestBundleForAction } from '../../../lib/voice/suggestBundle';
import { visibleConvertProjects } from '../../../lib/voice/convertProjectFilter';
import EntityUpdateDiff from './EntityUpdateDiff';

// ── B6: locale-aware department label resolver ──────────────────────
// CEO 2026-04-26: Department dropdown rendered Spanish labels in English
// mode. The departments table has `name` (English) + `name_es` (Spanish).
// Pick by current locale; gracefully fall back to whichever exists.
export function getDeptLabel(d = {}, locale = 'en') {
  const primary = locale === 'es' ? d.name_es : d.name;
  const secondary = locale === 'es' ? d.name : d.name_es;
  return primary || secondary || 'Unnamed';
}

// ── Action type colors ───────────────────────────────────────────────

export const ACTION_TYPE_COLORS = {
  // HEX_TODO[#9333EA] exception — see color-map.json
  bundle_item: { bg: 'rgba(147,51,234,0.12)', color: '#9333EA', label: 'Bundle' },
  // HEX_TODO[#3B82F6] exception — see color-map.json
  task:        { bg: 'rgba(59,130,246,0.12)',  color: '#3B82F6', label: 'Task' },
  follow_up:   { bg: 'rgba(249,115,22,0.12)',  color: 'var(--warn)', label: 'Follow-up' },
  po_line:     { bg: 'rgba(34,197,94,0.12)',   color: 'var(--ok)', label: 'PO Line' },
  // HEX_TODO[#14B8A6] exception — see color-map.json
  material:    { bg: 'rgba(20,184,166,0.12)',   color: '#14B8A6', label: 'Material' },
  // Quote Line — reuse the maritime accent (navy) so the chip reads as a
  // first-class sales action; converting routes into the quote scope-generator.
  // TICKET-1476: 'quote_item' is the derived_actions value; 'quote_line' kept for old rows.
  quote_item:  { bg: 'rgba(28,63,117,0.12)',   color: 'var(--accent)', label: 'Quote Line' },
  quote_line:  { bg: 'rgba(28,63,117,0.12)',   color: 'var(--accent)', label: 'Quote Line' },
  measurement: { bg: 'rgba(161,161,170,0.12)', color: 'var(--tm)', label: 'Measurement' },
  note:        { bg: 'rgba(113,113,122,0.12)', color: 'var(--tm)', label: 'Note' },
  decision:    { bg: 'rgba(16,185,129,0.12)',   color: 'var(--ok)', label: 'Decision' },
  question:    { bg: 'rgba(245,158,11,0.12)',   color: 'var(--warn)', label: 'Question' },
};

export const PRIORITY_COLORS = {
  P1: { bg: 'rgba(239,68,68,0.12)',  color: 'var(--bad)' },
  // HEX_TODO[#EAB308] exception — see color-map.json
  P2: { bg: 'rgba(234,179,8,0.12)',  color: '#EAB308' },
  P3: { bg: 'rgba(161,161,170,0.12)', color: 'var(--tm)' },
};

// ── Accepted action row ──────────────────────────────────────────────

// ── B5d: Category row with inline "Other → suggest a name" affordance ──
// Picking "Other" reveals an input. Submitting a non-empty name calls
// addProductCategory (RLS-gated to ceo/admin/pmo) and selects the new label.
// Submitting empty falls back to the General bucket — no row created.
function CategoryRow({ CATEGORIES, value, estimatedValue, onCategoryChange, onValueChange }) {
  const { t } = useT();
  const [otherInput, setOtherInput] = useState('');
  const [creating, setCreating] = useState(false);
  const isOther = value === 'Other';

  // If a saved value exists that isn't in the active list (e.g. just-created
  // custom one), inject it as an option so the select can render it.
  const extraValueOption = value && value !== 'Other' && !CATEGORIES.includes(value) ? value : null;

  const submitCustom = async () => {
    const name = otherInput.trim();
    if (!name) {
      onCategoryChange('General');
      setOtherInput('');
      return;
    }
    setCreating(true);
    try {
      const r = await addProductCategory(name);
      if (r?.success) {
        onCategoryChange(r.label || name);
        setOtherInput('');
      }
      // On RLS_DENIED or other failure, keep the input visible so user retries.
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-2">
        <label className="flex flex-col gap-1 flex-1">
          <span className="text-[11px] font-semibold text-tm">Category</span>
          <select value={value} onChange={(e) => onCategoryChange(e.target.value)}
            className="px-2.5 py-2 rounded-lg border border-bdr bg-glass text-t1 text-[13px] w-full cursor-pointer font-[inherit] outline-none">
            <option value="">Select...</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            {extraValueOption && <option value={extraValueOption}>{extraValueOption}</option>}
          </select>
        </label>
        <label className="flex flex-col gap-1 w-[100px]">
          <span className="text-[11px] font-semibold text-tm">Value</span>
          <input type="number" value={estimatedValue} onChange={(e) => onValueChange(e.target.value)}
            placeholder="0" className="px-2.5 py-2 rounded-lg border border-bdr bg-glass text-t1 text-[13px] w-full font-[inherit] outline-none" />
        </label>
      </div>
      {isOther && (
        <input
          autoFocus
          value={otherInput}
          onChange={(e) => setOtherInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitCustom(); } }}
          onBlur={submitCustom}
          disabled={creating}
          placeholder={t('breadcrumbCard.suggestCategoryPlaceholder')}
          className="px-2.5 py-2 rounded-lg border border-bdr bg-glass text-t1 text-[13px] w-full font-[inherit] outline-none mt-1"
        />
      )}
    </div>
  );
}

function AcceptedActionRow({ action, entityLinks, onNavigateToEntity }) {
  const aType = action.action_type || 'note';
  const typeInfo = ACTION_TYPE_COLORS[aType] || ACTION_TYPE_COLORS.note;
  const link = entityLinks[action.id] || (action.linked_task_id ? { type: 'task', id: action.linked_task_id } : null);

  return (
    <div key={action.id} style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '14px 16px', background: 'var(--glass, #f8fafb)',
      border: '1px solid rgba(0,0,0,0.06)', borderRadius: 10,
      opacity: 0.55, transition: 'opacity 200ms ease',
    }}>
      <CheckCircle2 size={14} color="var(--ok, #22C55E)" style={{ flexShrink: 0 }} />
      <span style={{
        padding: '3px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700,
        textTransform: 'uppercase', background: typeInfo.bg, color: typeInfo.color, flexShrink: 0,
      }}>
        {typeInfo.label}
      </span>
      <span style={{ fontSize: TYPOGRAPHY.bodySize, fontWeight: 600, color: 'var(--tm)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {action.title}
      </span>
      {link && (
        <button onClick={(e) => {
          e.stopPropagation();
          if (onNavigateToEntity) onNavigateToEntity(link);
        }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '8px 16px', borderRadius: 8, fontSize: 11, fontWeight: 600,
            border: 'none', cursor: 'pointer', minHeight: 44,
            // HEX_TODO[#3B82F6] exception — see color-map.json
            background: 'rgba(59,130,246,0.10)', color: '#3B82F6',
          }}>
          View <ArrowRight size={12} />
        </button>
      )}
    </div>
  );
}

// ── Pending action card with inline form ─────────────────────────────

// Subset of ACTION_TYPE_COLORS that the user can override TO. We only
// expose the types that resolve to a real entity downstream — `decision`
// and `question` exist as classifier outputs but you don't "convert" them,
// so they're not useful as user-facing override targets. CEO 2026-04-26:
// "wonder if the classifier gets it wrong whether it makes sense to
// override the suggested category — for instance task instead of bundle
// and vice versa. Just a click on top may save a full reprocess."
const TYPE_OVERRIDE_OPTIONS = ['task', 'bundle_item', 'quote_item', 'follow_up', 'po_line', 'material', 'note'];

function PendingActionCard({
  action, activeForm, formData, setFormData, convertingId, confirmingId,
  quickConvert, openForm, cancelForm, confirmAction, dismissAction, overrideType,
  people, projects = [], bundles = [], defaultProjectId, departments = [], onCreateProject, CATEGORIES,
}) {
  // B6 2026-04-26: Department dropdown rendered Spanish labels in English
  // mode. Read locale from useT() and pick the right column per row.
  const { t, locale } = useT();

  // Bug A 2026-04-26: optimistic type-pill flip. The previous code awaited
  // the API round-trip (1.1-2.4s on Vercel) before the pill label changed,
  // so the user saw "TASK" for the full latency window after clicking
  // BUNDLE. Track type locally; flip immediately on click. Server confirms
  // in background — on failure, the toast surfaces and we roll back.
  const [optimisticType, setOptimisticType] = useState(action.action_type || 'note');
  // Resync if the parent prop changes (e.g. server refresh brings authoritative state).
  useEffect(() => { setOptimisticType(action.action_type || 'note'); }, [action.action_type]);

  const typeInfo = ACTION_TYPE_COLORS[optimisticType] || ACTION_TYPE_COLORS.note;
  const pHint = action.priority_hint || 'P3';
  const pc = PRIORITY_COLORS[pHint] || PRIORITY_COLORS.P3;
  const isFormOpen = activeForm === action.id;
  const [typeChooserOpen, setTypeChooserOpen] = useState(false);
  const canOverride = typeof overrideType === 'function';

  // Phase D (TICKET-TASK-BUNDLE-STAGE-MODEL): bundle picker for task converts.
  // Fetch the selected project's bundles on demand while the accept form is
  // open for a task-type action. The task inherits the chosen bundle's stage
  // server-side; "General" (no bundle) leaves the task stage-less.
  const isTaskType = optimisticType !== 'bundle_item' && optimisticType !== 'material';
  // Bundles come from AppDataContext (via BreadcrumbCard) — shared entity, no
  // ad-hoc fetch (wiring-audit Rule 1). Filter to the selected project's live
  // bundles for the picker.
  const projectBundles = useMemo(() => (
    isTaskType && formData.project_id
      ? (bundles || []).filter(b => b.project_id === formData.project_id && !b.archived_at)
      : []
  ), [bundles, isTaskType, formData.project_id]);

  // ── Inline bundle quick-picker (CEO 2026-06-18) ──────────────────────
  // AI suggests the bundle; an inline dropdown overrides when it's wrong.
  // Resolve the action's project from its hints, then the voice record's
  // project (defaultProjectId), and filter the project's live bundles.
  const resolvedProjectId =
    action.user_edits?.project_id
    ?? action.detail_json?.project_id
    ?? defaultProjectId
    ?? '';
  const bundlesForConvert = useMemo(() => (
    isTaskType && resolvedProjectId
      ? (bundles || []).filter(
          b => (b.project_fk === resolvedProjectId || b.project_id === resolvedProjectId) && !b.archived_at,
        )
      : []
  ), [bundles, isTaskType, resolvedProjectId]);
  const suggestion = useMemo(
    () => suggestBundleForAction(action, bundlesForConvert),
    [action, bundlesForConvert],
  );
  const [selectedBundle, setSelectedBundle] = useState(() => suggestion?.id || '');
  const bundleTouchedRef = useRef(false);
  // Re-seed from the suggestion once bundles load — unless the user picked.
  useEffect(() => {
    if (!bundleTouchedRef.current) setSelectedBundle(suggestion?.id || '');
  }, [suggestion]);
  const showBundlePicker = bundlesForConvert.length > 0 && !isFormOpen;
  const isSuggested = !!suggestion && selectedBundle === suggestion.id;

  return (
    <div key={action.id} style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Action row */}
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 14,
        padding: '16px 20px', background: 'var(--card)',
        border: '1px solid rgba(0,0,0,0.10)', boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        borderRadius: isFormOpen ? '10px 10px 0 0' : 10,
        borderBottom: isFormOpen ? 'none' : undefined,
      }}>
        {/* Type + Priority + Buttons row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {canOverride ? (
            <button
              data-testid="action-type-pill"
              onClick={(e) => { e.stopPropagation(); setTypeChooserOpen(prev => !prev); }}
              title={t('breadcrumbCard.tapToChangeType')}
              style={{
                padding: '3px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.05em',
                background: typeInfo.bg, color: typeInfo.color,
                border: 'none', cursor: 'pointer', minHeight: 22,
              }}>
              {typeInfo.label}
            </button>
          ) : (
            <span style={{
              padding: '3px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.05em',
              background: typeInfo.bg, color: typeInfo.color,
            }}>
              {typeInfo.label}
            </span>
          )}
          <span style={{
            padding: '3px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700,
            background: pc.bg, color: pc.color,
          }}>
            {pHint}
          </span>
          <div style={{ flex: 1 }} />
          {/* Quick Convert / Accept / Dismiss — 44px touch targets */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {['task', 'bundle_item', 'quote_item', 'material', 'follow_up', 'po_line'].includes(optimisticType) && (
              <button
                data-testid="breadcrumb-action-convert"
                onClick={(e) => { e.stopPropagation(); quickConvert(action, bundlesForConvert.length > 0 ? selectedBundle : undefined); }}
                disabled={convertingId === action.id}
                title={`Quick create ${typeInfo.label}`}
                style={{
                  minHeight: 44, padding: '0 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                  background: 'var(--navy-surface)', color: 'var(--on-navy)', fontSize: 11, fontWeight: 600,
                  opacity: convertingId === action.id ? 0.5 : 1,
                }}>
                {convertingId === action.id ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <ArrowRight size={14} />}
                {convertingId === action.id ? '' : 'Convert'}
              </button>
            )}
            <button onClick={(e) => { e.stopPropagation(); openForm(action); }} title="Edit & Accept"
              style={{ width: 44, height: 44, borderRadius: 8, border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(34,197,94,0.10)', color: 'var(--ok)' }}>
              <Check size={16} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); dismissAction(action.id); }} title="Dismiss"
              style={{ width: 44, height: 44, borderRadius: 8, border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(239,68,68,0.10)', color: 'var(--bad)' }}>
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Type chooser — appears below the type pill row when opened */}
        {canOverride && typeChooserOpen && (
          <div data-testid="action-type-chooser" style={{
            display: 'flex', flexWrap: 'wrap', gap: 6,
            padding: '8px 10px', borderRadius: 8,
            background: 'var(--glass)', border: '1px solid var(--bdr)',
          }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--tm)', textTransform: 'uppercase', letterSpacing: '0.05em', alignSelf: 'center', marginRight: 4 }}>
              Change type:
            </span>
            {TYPE_OVERRIDE_OPTIONS.map(opt => {
              const info = ACTION_TYPE_COLORS[opt] || ACTION_TYPE_COLORS.note;
              const isCurrent = opt === optimisticType;
              return (
                <button
                  key={opt}
                  data-testid={`action-type-option-${opt}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isCurrent) { setTypeChooserOpen(false); return; }
                    // Bug A: flip the local pill state synchronously, BEFORE
                    // awaiting the API. The user sees the type change instantly.
                    setOptimisticType(opt);
                    setTypeChooserOpen(false);
                    // Fire-and-forget — overrideType handles its own toasting.
                    overrideType(action.id, opt);
                  }}
                  disabled={isCurrent}
                  style={{
                    padding: '6px 10px', borderRadius: 999, fontSize: 10, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                    background: info.bg, color: info.color,
                    border: isCurrent ? `1px solid ${info.color}` : 'none',
                    cursor: isCurrent ? 'default' : 'pointer',
                    opacity: isCurrent ? 0.55 : 1,
                    minHeight: 28,
                  }}>
                  {info.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Title */}
        <div style={{ fontSize: TYPOGRAPHY.bodySize, fontWeight: 600, color: 'var(--t1)', lineHeight: 1.4 }}>
          {action.title}
        </div>

        {/* Description/notes (body of the action) */}
        {action.description && (
          <div style={{ fontSize: TYPOGRAPHY.bodySize, color: 'var(--t2)', lineHeight: 1.6 }}>
            {action.description}
          </div>
        )}

        {/* Assignee + due hints */}
        {(action.assignee_hint || action.due_hint) && (
          <div className="flex items-center gap-3" style={{ fontSize: BREADCRUMB.fontSize, color: 'var(--tm)' }}>
            {action.assignee_hint && <span className="flex items-center gap-1"><Users size={12} /> {action.assignee_hint}</span>}
            {action.due_hint && <span className="flex items-center gap-1"><Clock size={12} /> {action.due_hint}</span>}
          </div>
        )}

        {/* Inline bundle quick-picker — pre-filled with the AI suggestion, the
            user overrides via the dropdown. Convert sends the chosen bundle. */}
        {showBundlePicker && (
          <div data-testid="breadcrumb-action-bundle-row"
            style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--tm)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Package size={12} /> {t('breadcrumbCard.bundleLabel')}
            </span>
            <select
              data-testid="breadcrumb-action-bundle-select"
              value={selectedBundle}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => { bundleTouchedRef.current = true; setSelectedBundle(e.target.value); }}
              style={{
                minHeight: 44, flex: 1, minWidth: 140, padding: '8px 10px', borderRadius: 8,
                border: '1px solid var(--bdr)', background: 'var(--glass)', color: 'var(--t1)',
                fontSize: 13, cursor: 'pointer', outline: 'none',
              }}>
              <option value="">{t('breadcrumbCard.noBundle')}</option>
              {bundlesForConvert.map(b => (
                <option key={b.id} value={b.id}>{b.title || b.name || 'Bundle'}</option>
              ))}
            </select>
            {isSuggested && (
              <span data-testid="breadcrumb-action-bundle-suggested"
                style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                <Sparkles size={11} /> {t('breadcrumbCard.suggested')}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Accept Form (context-aware) ── */}
      {isFormOpen && (() => {
        const isBundle = optimisticType === 'bundle_item' || optimisticType === 'material';
        return (
        <div data-testid="action-form"
          className="rounded-b-[10px] bg-card border border-bdr border-t border-t-bdr border-t-dashed flex flex-col gap-3.5"
          style={{ borderTopStyle: 'dashed', padding: '20px 20px 24px' }}>
          {/* Title */}
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold text-tm">{isBundle ? 'Bundle Name' : 'Title'}</span>
            <input type="text" value={formData.title || ''} onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
              className="px-2.5 py-2 rounded-lg border border-bdr bg-glass text-t1 text-[13px] w-full font-[inherit] outline-none" />
          </label>

          {/* Description / Scope */}
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold text-tm">{isBundle ? 'Scope / Description' : 'Notes / Body'}</span>
            <textarea value={formData.description || ''} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
              rows={3} className="px-2.5 py-2 rounded-lg border border-bdr bg-glass text-t1 text-[13px] w-full resize-y font-[inherit] outline-none" />
          </label>

          {/* Bundle-specific: Category + Estimated Value */}
          {isBundle && (
            <CategoryRow
              CATEGORIES={CATEGORIES}
              value={formData.product_category || ''}
              estimatedValue={formData.estimated_value || ''}
              onCategoryChange={(v) => setFormData(p => ({ ...p, product_category: v }))}
              onValueChange={(v) => setFormData(p => ({ ...p, estimated_value: v }))}
            />
          )}

          {/* Task-specific: Assignee + Priority */}
          {!isBundle && (
            <div className="flex gap-2">
              <label className="flex flex-col gap-1 flex-1">
                <span className="text-[11px] font-semibold text-tm">Assignee</span>
                <select value={formData.assignee || ''} onChange={(e) => setFormData(p => ({ ...p, assignee: e.target.value }))}
                  className="px-2.5 py-2 rounded-lg border border-bdr bg-glass text-t1 text-[13px] w-full cursor-pointer font-[inherit] outline-none">
                  <option value="">Unassigned</option>
                  {people.map(p => (
                    <option key={p.id} value={p.name}>{p.name}{p.role ? ` (${p.role})` : ''}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 w-20">
                <span className="text-[11px] font-semibold text-tm">Priority</span>
                <select value={formData.priority || 'P2'} onChange={(e) => setFormData(p => ({ ...p, priority: e.target.value }))}
                  className="px-2.5 py-2 rounded-lg border border-bdr bg-glass text-t1 text-[13px] w-full cursor-pointer font-[inherit] outline-none">
                  <option value="P1">P1</option>
                  <option value="P2">P2</option>
                  <option value="P3">P3</option>
                </select>
              </label>
            </div>
          )}

          {/* Project picker — shown for ALL action types. Bundles MUST have a
              parent project (otherwise they orphan and don't render in any
              project's Bundles tab — CEO bug 2026-04-25). Non-bundle action
              types also benefit from optional project + department linking. */}
          <div className="flex gap-2">
            <label className="flex flex-col gap-1 flex-1">
              <span className="text-[11px] font-semibold text-tm">
                Project{isBundle ? ' *' : ''}
              </span>
              <select
                data-testid="action-project-select"
                value={formData.project_id || ''}
                onChange={async (e) => {
                  const v = e.target.value;
                  if (v === '__create__') {
                    const name = typeof window !== 'undefined' && typeof window.prompt === 'function'
                      ? window.prompt('New project name')
                      : '';
                    if (!name || !name.trim()) return;
                    try {
                      const created = typeof onCreateProject === 'function'
                        ? await onCreateProject({ project_name: name.trim() })
                        : null;
                      const newId = created?.id || created?.project_id || '';
                      setFormData(p => ({ ...p, project_id: newId, bundle_id: '' }));
                    } catch (err) {
                      console.error('[BreadcrumbActions] create project failed:', err);
                    }
                    return;
                  }
                  setFormData(p => ({ ...p, project_id: v, bundle_id: '' }));
                }}
                className="px-2.5 py-2 rounded-lg border border-bdr bg-glass text-t1 text-[13px] w-full cursor-pointer font-[inherit] outline-none">
                <option value="">{isBundle ? 'Pick a project…' : 'No project'}</option>
                {visibleConvertProjects(projects, formData.project_id)
                  .map(p => (
                    <option key={p.id} value={p.id}>{p.project_name || 'Untitled'}</option>
                  ))}
                {typeof onCreateProject === 'function' && (
                  <option value="__create__">+ Create new project…</option>
                )}
              </select>
            </label>
            {!isBundle && (
              <label className="flex flex-col gap-1 flex-1">
                <span className="text-[11px] font-semibold text-tm">Department</span>
                <select
                  data-testid="action-department-select"
                  value={formData.department_id || ''}
                  onChange={(e) => setFormData(p => ({ ...p, department_id: e.target.value }))}
                  className="px-2.5 py-2 rounded-lg border border-bdr bg-glass text-t1 text-[13px] w-full cursor-pointer font-[inherit] outline-none">
                  <option value="">None</option>
                  {departments.map(d => (
                    <option key={d.id} value={d.id}>{getDeptLabel(d, locale)}</option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {/* Bundle picker (task converts only) — Phase D. A bundle attaches
              the task to that subproject and inherits the bundle's current
              stage. "General" leaves the task at project level (no bundle). */}
          {!isBundle && formData.project_id && (
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold text-tm">Bundle</span>
              <select
                data-testid="action-bundle-select"
                value={formData.bundle_id || ''}
                onChange={(e) => setFormData(p => ({ ...p, bundle_id: e.target.value }))}
                className="px-2.5 py-2 rounded-lg border border-bdr bg-glass text-t1 text-[13px] w-full cursor-pointer font-[inherit] outline-none">
                <option value="">General (no bundle)</option>
                {projectBundles.map(b => (
                  <option key={b.id} value={b.id}>{b.title || b.name || 'Bundle'}</option>
                ))}
              </select>
            </label>
          )}

          {/* Due date (shown for both) */}
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold text-tm">Due Date</span>
            <div className="flex gap-1.5 items-center">
              <input type="date" value={formData.due_date || ''} onChange={(e) => setFormData(p => ({ ...p, due_date: e.target.value }))}
                className="px-2.5 py-2 rounded-lg border border-bdr bg-glass text-t1 text-[13px] flex-1 font-[inherit] outline-none" />
              <button type="button" onClick={() => setFormData(p => ({ ...p, due_date: new Date().toISOString().split('T')[0] }))}
                style={{ padding: '8px 12px', minHeight: 44, borderRadius: 8, background: 'var(--glass)', border: '1px solid var(--bdr)', color: 'var(--accent)', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Today
              </button>
            </div>
          </label>

          {/* Confirm / Cancel */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 4, paddingTop: 12, borderTop: '1px solid var(--bdr)' }}>
            <button onClick={cancelForm}
              style={{ padding: '10px 20px', minHeight: 44, borderRadius: 8, fontSize: TYPOGRAPHY.bodySize, fontWeight: 600, border: 'none', cursor: 'pointer', background: 'var(--stodob)', color: 'var(--tm)' }}>
              Cancel
            </button>
            <button
              data-testid="action-confirm"
              onClick={() => confirmAction(action.id)}
              disabled={confirmingId === action.id}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '10px 20px', minHeight: 44, borderRadius: 8,
                fontSize: TYPOGRAPHY.bodySize, fontWeight: 600, border: 'none',
                cursor: confirmingId === action.id ? 'default' : 'pointer',
                background: 'var(--accent)', color: 'var(--card)',
                opacity: confirmingId === action.id ? 0.6 : 1,
              }}>
              {confirmingId === action.id
                ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                : <Check size={14} />}
              {confirmingId === action.id ? 'Creating…' : `Create ${typeInfo.label}`}
            </button>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

// ── Dismissed actions section ────────────────────────────────────────

function DismissedActionsSection({ dismissedActions, onActionUpdate, onRefresh, updateAction }) {
  const [dismissedExpanded, setDismissedExpanded] = useState(false);

  if (dismissedActions.length === 0) return null;

  return (
    <div style={{ borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: 10, marginTop: 4 }}>
      <button onClick={() => setDismissedExpanded(prev => !prev)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, width: '100%',
          padding: '6px 0', background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 11, fontWeight: 600, color: 'var(--tm, #8896AB)',
        }}>
        {dismissedExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        Dismissed ({dismissedActions.length})
      </button>
      {dismissedExpanded && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
          {dismissedActions.map(action => {
            const aType = action.action_type || 'note';
            const typeInfo = ACTION_TYPE_COLORS[aType] || ACTION_TYPE_COLORS.note;
            return (
              <div key={action.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 16px', borderRadius: 8,
                opacity: 0.4, transition: 'opacity 200ms ease',
              }}>
                <XCircle size={14} color="var(--bad, #EF4444)" style={{ flexShrink: 0 }} />
                <span style={{
                  padding: '3px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700,
                  textTransform: 'uppercase', background: typeInfo.bg, color: typeInfo.color, flexShrink: 0,
                }}>
                  {typeInfo.label}
                </span>
                <span style={{
                  fontSize: BREADCRUMB.fontSize, color: 'var(--tm)', flex: 1, minWidth: 0,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  textDecoration: 'line-through',
                }}>
                  {action.title}
                </span>
                <button
                  data-testid={`bc-reopen-${action.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    (async () => {
                      try {
                        const payload = { actionId: action.id, operation: 'reopen' };
                        if (onActionUpdate) await onActionUpdate(payload);
                        else await updateAction(payload);
                        if (onRefresh) onRefresh();
                      } catch (err) { console.error('[BreadcrumbCard] reopen error:', err); }
                    })();
                  }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '8px 14px', borderRadius: 8, fontSize: 10, fontWeight: 600,
                    border: 'none', cursor: 'pointer', minHeight: 44,
                    background: 'rgba(59,130,246,0.08)', color: 'var(--accent, #1C3F75)',
                  }}>
                  <RotateCcw size={10} /> Reopen
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main Actions List component ──────────────────────────────────────

export default function BreadcrumbActions({
  pendingActions, undoBarActions, acceptedActions, dismissedActions,
  acceptedCount, pendingCount, dismissedCount,
  entityLinks, onNavigateToEntity,
  activeForm, formData, setFormData, convertingId, confirmingId,
  quickConvert, openForm, cancelForm, confirmAction, dismissAction, undoDismiss,
  overrideType,
  people, projects, bundles, defaultProjectId, departments, onCreateProject, CATEGORIES,
  onActionUpdate, onRefresh, updateAction,
}) {
  const hasActions = pendingActions.length > 0 || undoBarActions.length > 0
    || acceptedActions.length > 0 || dismissedActions.length > 0;

  if (!hasActions) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, background: 'var(--bg)', borderRadius: 12, padding: 20, border: '1px solid rgba(0,0,0,0.06)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tm, #8896AB)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Actions
        <span style={{ fontWeight: 400, textTransform: 'none', marginLeft: 8, fontSize: 10, letterSpacing: 0 }}>
          {acceptedCount > 0 && <span style={{ color: 'var(--ok, #22C55E)' }}>{acceptedCount} accepted</span>}
          {acceptedCount > 0 && pendingCount > 0 && ' \u00b7 '}
          {pendingCount > 0 && <span>{pendingCount} pending</span>}
          {(acceptedCount > 0 || pendingCount > 0) && dismissedCount > 0 && ' \u00b7 '}
          {dismissedCount > 0 && <span>{dismissedCount} dismissed</span>}
        </span>
      </div>

      {/* Undo bars for pending dismissals */}
      {undoBarActions.map(a => (
        <div key={`undo-${a.id}`} style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 16px', borderRadius: 10,
          background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)',
        }}>
          <span style={{ fontSize: BREADCRUMB.fontSize, color: 'var(--t2)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <span style={{ textDecoration: 'line-through' }}>{a.title}</span> — dismissed
          </span>
          <button onClick={() => undoDismiss(a.id)}
            style={{ padding: '8px 16px', borderRadius: 8, fontSize: 11, fontWeight: 600, border: 'none', cursor: 'pointer', background: 'var(--accent)', color: 'var(--card)', minHeight: 44 }}>
            Undo
          </button>
        </div>
      ))}

      {/* Accepted actions */}
      {acceptedActions.map(action => (
        <AcceptedActionRow key={action.id} action={action} entityLinks={entityLinks} onNavigateToEntity={onNavigateToEntity} />
      ))}

      {/* Pending actions */}
      {pendingActions.map(action => (action.action_type === 'update_entity' ? (
        <EntityUpdateDiff
          key={action.id}
          action={action}
          busy={convertingId === action.id}
          onAccept={quickConvert}
          onDismiss={dismissAction}
        />
      ) : (
        <PendingActionCard
          key={action.id}
          action={action}
          activeForm={activeForm}
          formData={formData}
          setFormData={setFormData}
          convertingId={convertingId}
          confirmingId={confirmingId}
          quickConvert={quickConvert}
          openForm={openForm}
          cancelForm={cancelForm}
          confirmAction={confirmAction}
          dismissAction={dismissAction}
          overrideType={overrideType}
          people={people}
          projects={projects}
          bundles={bundles}
          defaultProjectId={defaultProjectId}
          departments={departments}
          onCreateProject={onCreateProject}
          CATEGORIES={CATEGORIES}
        />
      )))}

      {/* Dismissed actions */}
      <DismissedActionsSection
        dismissedActions={dismissedActions}
        onActionUpdate={onActionUpdate}
        onRefresh={onRefresh}
        updateAction={updateAction}
      />
    </div>
  );
}
